import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RealTimeHeader = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Simulate connection status changes
  useEffect(() => {
    const interval = setInterval(() => {
      const shouldDisconnect = Math.random() < 0.03;
      if (shouldDisconnect && connectionStatus === 'connected') {
        setConnectionStatus('disconnected');
        setTimeout(() => {
          setConnectionStatus('connected');
        }, 2000 + Math.random() * 3000);
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [connectionStatus]);

  const handleEmergencyRefresh = async () => {
    setIsRefreshing(true);
    setConnectionStatus('updating');
    
    setTimeout(() => {
      setConnectionStatus('connected');
      setIsRefreshing(false);
      window.dispatchEvent(new CustomEvent('emergencyRefresh'));
    }, 1500);
  };

  const getStatusConfig = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          icon: 'Wifi',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Live',
          pulse: false
        };
      case 'updating':
        return {
          icon: 'RefreshCw',
          color: 'text-primary',
          bgColor: 'bg-primary/10',
          label: 'Updating',
          pulse: true
        };
      case 'disconnected':
        return {
          icon: 'WifiOff',
          color: 'text-error',
          bgColor: 'bg-error/10',
          label: 'Offline',
          pulse: true
        };
      default:
        return {
          icon: 'Wifi',
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          label: 'Unknown',
          pulse: false
        };
    }
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="bg-card border-b border-border px-4 lg:px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Live Status Section */}
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-3">
            <div className={`flex items-center justify-center w-8 h-8 rounded-full ${statusConfig.bgColor}`}>
              <Icon 
                name={statusConfig.icon} 
                size={16} 
                className={`${statusConfig.color} ${statusConfig.pulse ? 'animate-pulse' : ''} ${connectionStatus === 'updating' ? 'animate-spin' : ''}`}
              />
            </div>
            <div className="flex flex-col">
              <span className={`text-sm font-semibold ${statusConfig.color}`}>
                {statusConfig.label}
              </span>
              <span className="text-xs text-muted-foreground">
                Real-time monitoring
              </span>
            </div>
          </div>

          {/* Current Time Display */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-2 bg-muted rounded-lg">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className="text-xs text-muted-foreground">
                {currentTime.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Emergency Actions */}
        <div className="flex items-center space-x-3">
          {/* Mobile Time Display */}
          <div className="md:hidden text-right">
            <div className="text-sm font-medium text-foreground">
              {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-xs text-muted-foreground">
              {currentTime.toLocaleDateString([], { month: 'short', day: 'numeric' })}
            </div>
          </div>

          {/* Emergency Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleEmergencyRefresh}
            disabled={isRefreshing}
            className="flex items-center space-x-2"
          >
            <Icon 
              name="RefreshCw" 
              size={16} 
              className={isRefreshing ? 'animate-spin' : ''}
            />
            <span className="hidden sm:inline">Emergency Refresh</span>
          </Button>

          {/* System Health Indicator */}
          <div className="hidden lg:flex items-center space-x-2 px-3 py-2 bg-success/10 rounded-lg">
            <Icon name="Activity" size={16} className="text-success" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-success">
                System Healthy
              </span>
              <span className="text-xs text-muted-foreground">
                All services operational
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeHeader;